import { convertMinToDays, DAYS_IN_WEEK, DAYS_IN_MONTH } from 'utils/time'
import { PARTICIPANT_VISIT_TABLE_KEY_NAME_MAP } from '../../ParticipantVisitPage/utils/participantVisitConstants'

const DAY_RELATIVITY_TYPE_MAP = {
  joins: 'joins',
  lastVisit: 'lastVisit',
  completesVisit: 'completesVisit',
  completesCycle: 'completesCycle',
  startsCycle: 'startsCycle',
}

const calculateVisitDay = ({ dayVal, selectedVisitDayRelativity, visitScheduleArr, selectedVisit, currentVisitId }) => {
  if (dayVal == null || dayVal === '') return null
  const dayValNum = Number(dayVal)
  switch (selectedVisitDayRelativity) {
    case DAY_RELATIVITY_TYPE_MAP.completesVisit: {
      const selectedVisitDay = visitScheduleArr.filter(visit => visit.id === selectedVisit)[0]?.visit_day || 0
      return dayValNum + selectedVisitDay
    }
    case DAY_RELATIVITY_TYPE_MAP.lastVisit: {
      const largestDay =
        visitScheduleArr.filter(visit => visit.id !== currentVisitId).sort((a, d) => d.visit_day - a.visit_day)[0]
          ?.visit_day || 0
      return largestDay + dayValNum
    }
    case DAY_RELATIVITY_TYPE_MAP.joins:
    default:
      return dayValNum
  }
}

export const addPriorVisits = (arr, id, priorVisits = {}) => {
  if (!id) return priorVisits
  const currVisit = arr.find(v => v.id === id)
  if (!currVisit) return priorVisits
  const nextPriorVisits = { ...priorVisits, [currVisit.id]: currVisit.visit_day }

  if (
    [DAY_RELATIVITY_TYPE_MAP.completesVisit, DAY_RELATIVITY_TYPE_MAP.lastVisit].includes(
      currVisit.visit_metadata?.deployment_option,
    )
  ) {
    const filterVisits = arr.filter(v => v.id !== id)
    return addPriorVisits(filterVisits, currVisit.visit_metadata?.relative_visit_id, nextPriorVisits)
  }
  return nextPriorVisits
}

export const checkIfThereIsOverLap = ({ currentVisitDay, selectedVisitDayMap, templateVisitId }) => {
  const visitDayMap = { ...selectedVisitDayMap, [templateVisitId]: currentVisitDay }
  const daysArr = Object.values(visitDayMap)
  let count = 0
  for (let i = 0; i < daysArr.length; i++) {
    if (daysArr[i] === currentVisitDay) count += 1
    if (count > 1) return true
  }
  return false
}

const _formatIntoTableRow = (templateVisit, allCycles, selectedCycleId, studyVisitSchedule, isNewVisitVersion) => {
  const { name, visit_day, day, id, visit_metadata = {} } = templateVisit
  const anchorCycleId = visit_metadata?.relative_cycle_id
  const anchorVisitId = visit_metadata?.relative_visit_id
  const deploymentOption = visit_metadata?.deployment_option

  const visitDayValue = visit_day ?? day ?? 0

  let computedVisitDay
  if (allCycles.length || (isNewVisitVersion && !selectedCycleId)) {
    // Arms / visit-cycle mode, or first Arm visit without a cycle:
    // delegate to getComputedStudyDay so we get visitDay + 1 behavior
    // for non-cycled "joins study" visits.
    // eslint-disable-next-line no-use-before-define
    computedVisitDay = getComputedStudyDay({
      allCycles,
      selectedCycleId,
      visitDayValue,
      selectedVisitId: id,
      anchorCycleId,
      anchorVisitId,
      deploymentOption,
    })
  } else {
    // Legacy (non-Arms) behavior – keep existing calculateVisitDay logic.
    computedVisitDay = calculateVisitDay({
      dayVal: visitDayValue,
      selectedVisitDayRelativity: deploymentOption,
      visitScheduleArr: studyVisitSchedule[0].visits,
      selectedVisit: anchorVisitId,
      currentVisitId: id,
    })
  }

  const visitDay = computedVisitDay ?? 'Enter a visit day'

  return [
    {
      key: 'visitName',
      value: name || 'Enter a visit name',
      sortValue: name,
      visitId: null,
      className: 'current-visit',
    },
    {
      key: 'visitDay',
      value: visitDay,
      sortValue: visitDay,
      className: 'visit-day-col current-visit',
    },
  ]
}

export const generateUpdatedTable = ({
  templateVisit,
  initialVisits,
  isEdit,
  filter,
  allCycles,
  selectedCycleId,
  studyVisitSchedule,
  isNewVisitVersion,
}) => {
  const row = _formatIntoTableRow(templateVisit, allCycles, selectedCycleId, studyVisitSchedule, isNewVisitVersion)
  let tableData = null
  if (!isEdit) {
    tableData = [...Object.values(initialVisits), row]
  } else {
    const { id } = templateVisit
    tableData = Object.values({ ...initialVisits, [id]: row })
  }
  if (!filter) return tableData

  const [filterKey, filterVal] = Object.entries(filter)[0]
  let colIndex = null
  return tableData.filter(dataRow => {
    if (colIndex === null) colIndex = dataRow.findIndex(col => col.key === filterKey)
    return dataRow[colIndex].value === filterVal
  })
}

export const getIsDifferentDay = (moment1, moment2) => {
  if (!moment1 || !moment2) return false
  return !moment1.isSame(moment2, 'day')
}

export const getDayDifference = (moment1, moment2) => {
  if (!moment1 || !moment2) return 0
  return moment2.startOf('day').diff(moment1.startOf('day'), 'days')
}

export const getVisitWindowDeviation = (dayDifference, visitWindow) => {
  return Math.abs(dayDifference) > visitWindow
}

export const getVisitDeviationMap = ptpVisits => {
  const deviatedVisitMap = {}
  ptpVisits.forEach(visit => {
    const proposedVisitCell = visit[PARTICIPANT_VISIT_TABLE_KEY_NAME_MAP.proposedDay.index]
    if (proposedVisitCell?.hasDeviation) {
      deviatedVisitMap[visit[PARTICIPANT_VISIT_TABLE_KEY_NAME_MAP.visitName.index].visitId] = proposedVisitCell.value
    }
  })
  return deviatedVisitMap
}

export const getNewMetadata = ({ participantVisit = {}, dayDifference, subsequentVisit }) => {
  const newMetadata = {}
  if (subsequentVisit) {
    const { participant_visit_metadata: prevMetadata = {}, visit_datetime, visit_day, visit_window } = subsequentVisit
    const { original_visit_datetime, original_visit_day } = prevMetadata
    const originalVisitDay = original_visit_day || visit_day
    const originalVisitDatetime = original_visit_datetime || visit_datetime

    newMetadata.original_visit_datetime = originalVisitDatetime
    newMetadata.original_visit_day = originalVisitDay
    newMetadata.visit_window_deviation = getVisitWindowDeviation(dayDifference, visit_window)
    newMetadata.new_visit_day = originalVisitDay + dayDifference
  } else {
    const { visit_metadata: prevMetadata = {}, visitDatetime, visitDay, visitWindow } = participantVisit
    const { original_visit_datetime, original_visit_day } = prevMetadata
    const originalVisitDay = original_visit_day ?? visitDay
    newMetadata.original_visit_datetime = original_visit_datetime ?? visitDatetime
    newMetadata.original_visit_day = originalVisitDay
    newMetadata.visit_window_deviation = getVisitWindowDeviation(dayDifference, visitWindow)
    newMetadata.new_visit_day = originalVisitDay + dayDifference
  }
  return newMetadata
}

const findVisitAcrossCycles = (cycles, visitId) => {
  if (!visitId || !cycles?.length) return null
  return cycles.reduce((found, cycle) => {
    if (found) return found
    return cycle?.visits?.find(v => String(v?.id) === String(visitId)) || null
  }, null)
}

const getVisitStudyDayFromCycleData = visit => {
  if (!visit) return null
  const computed = visit.metadata?.computed_study_day
  if (Number.isFinite(computed)) return computed
  const day = visit.day ?? visit.visit_day
  if (day != null && day !== '') {
    return Number(day) + 1
  }
  return null
}

export function getComputedStudyDay({
  allCycles,
  selectedCycleId,
  visitDayValue = 0,
  selectedVisitId,
  anchorCycleId,
  anchorVisitId,
  deploymentOption,
}) {
  const isSameId = (idA, idB) => String(idA) === String(idB)

  const getAnchorCycle = () => {
    if (deploymentOption === 'completesCycle') {
      return allCycles?.find(item => isSameId(item?.id, anchorCycleId))
    }

    if (deploymentOption === 'completesVisit') {
      const cycle = allCycles?.find(item => {
        if (item?.visits?.find(visit => isSameId(visit?.id, anchorVisitId))) {
          return true
        }
        return false
      })

      return cycle
    }
  return undefined
  }

  const isNonCycledVisit = !selectedCycleId

  const currentCycle = allCycles?.find(item => isSameId(item?.id, selectedCycleId))
  const anchorCycle = getAnchorCycle()

  const currentCycleProjectedStudyDay = currentCycle?.metadata?.projected_study_day || 0
  const currentCycleProjectedStudyEndDay = anchorCycle?.metadata?.projected_study_end_day || 0

  const currentVisit = currentCycle?.visits?.find(visit => isSameId(visit?.id, selectedVisitId))
  const savedVisit = currentVisit || (selectedVisitId ? findVisitAcrossCycles(allCycles, selectedVisitId) : null)
  const savedDay = savedVisit?.day ?? savedVisit?.visit_day
  const savedDeploymentOption = savedVisit?.metadata?.deployment_option
  const savedComputedStudyDay = savedVisit?.metadata?.computed_study_day

  const hasSameSchedulingInputs =
    savedVisit &&
    Number(savedDay) === Number(visitDayValue) &&
    (savedDeploymentOption || 'joins') === (deploymentOption || 'joins') &&
    isSameId(savedVisit?.metadata?.relative_visit_id || '', anchorVisitId || '') &&
    isSameId(savedVisit?.metadata?.relative_cycle_id || '', anchorCycleId || '')

  const isJoinsNonCycledVisit =
    isNonCycledVisit && (deploymentOption === 'joins' || !deploymentOption)

  // Use persisted study day only while scheduling inputs still match saved data.
  // When the user edits visit day / anchor, recalculate for live preview and validation.
  // Non-cycled "joins study" visits always recalculate — backend computed_study_day can be
  // wrong when a stale anchor_id anchored the dummy cycle after a real cycle.
  if (hasSameSchedulingInputs && Number.isFinite(savedComputedStudyDay) && !isJoinsNonCycledVisit) {
    return savedComputedStudyDay
  }

  if (deploymentOption === 'completesCycle') {
    return visitDayValue + currentCycleProjectedStudyEndDay + 1
  }

  if (isNonCycledVisit) {
    if (deploymentOption === 'completesVisit' && anchorVisitId) {
      const anchorVisit = findVisitAcrossCycles(allCycles, anchorVisitId)
      const anchorStudyDay = getVisitStudyDayFromCycleData(anchorVisit)
      if (anchorStudyDay != null) {
        return visitDayValue + anchorStudyDay
      }
    }
    return visitDayValue + 1
  }

  return visitDayValue + currentCycleProjectedStudyDay + 1
}

/**
 * Normalize cycle time unit keys from the duration/occur dropdowns.
 * @param {string} timeUnit
 * @returns {'days' | 'weeks' | 'months'}
 */
export const normalizeCycleTimeUnit = (timeUnit = 'days') => {
  const unit = String(timeUnit).toLowerCase()
  if (unit.includes('month')) return 'months'
  if (unit.includes('week')) return 'weeks'
  return 'days'
}

/**
 * Convert cycle duration / occur form values to whole days (days, weeks, or months).
 * @param {number | string} value
 * @param {string} timeUnit
 * @returns {number}
 */
export const convertCycleTimeToDays = (value, timeUnit = 'days') => {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return 0
  switch (normalizeCycleTimeUnit(timeUnit)) {
    case 'months':
      return num * DAYS_IN_MONTH
    case 'weeks':
      return num * DAYS_IN_WEEK
    default:
      return num
  }
}

/**
 * Like convertCycleTimeToDays but allows 0 (for "days after" gap).
 * @param {number | string} value
 * @param {string} timeUnit
 * @returns {number}
 */
export const convertCycleOccurToDays = (value, timeUnit = 'days') => {
  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) return 0
  switch (normalizeCycleTimeUnit(timeUnit)) {
    case 'months':
      return num * DAYS_IN_MONTH
    case 'weeks':
      return num * DAYS_IN_WEEK
    default:
      return num
  }
}

/**
 * Convert cycle form values to stored length_value / start_time (minutes).
 * @param {number | string} value
 * @param {string} timeUnit
 * @returns {number}
 */
export const convertCycleTimeToMinutes = (value, timeUnit = 'days') => {
  return convertMinToDays(convertCycleTimeToDays(value, timeUnit))
}

export const convertCycleOccurToMinutes = (value, timeUnit = 'days') => {
  return convertMinToDays(convertCycleOccurToDays(value, timeUnit))
}

const getCycleAnchorId = cycle =>
  cycle?.anchor_id ?? cycle?.cycle_anchor_id ?? cycle?.metadata?.anchor_cycle_id ?? ''

export const normalizeCycleAnchorOption = (anchorOption, anchorId) => {
  if (anchorOption === 'cycle' || anchorOption === 'visit' || anchorOption === 'participant_start') {
    return anchorOption
  }
  if (anchorId) return 'cycle'
  return 'participant_start'
}

export const getCycleDurationDaysFromLengthValue = lengthValueMinutes => {
  if (lengthValueMinutes == null || lengthValueMinutes === '') return null
  const mins = Number(lengthValueMinutes)
  if (!Number.isFinite(mins) || mins < 0) return null
  return Math.max(1, Math.floor(convertMinToDays(mins, false)))
}

export const getCycleOccurGapDaysFromStartTime = startTimeMinutes => {
  if (startTimeMinutes == null || startTimeMinutes === '') return 0
  const mins = Number(startTimeMinutes)
  if (!Number.isFinite(mins) || mins < 0) return 0
  return Math.floor(convertMinToDays(mins, false))
}

/**
 * Resolve projected_study_day / projected_study_end_day from metadata, or from
 * length_value + start_time + anchor chain when metadata is absent (unsaved / API).
 * projected_study_end_day is exclusive (first day after the cycle).
 */
export const resolveProjectedStudyDaysForCycle = (cycle, allCycles = [], cache = new Map()) => {
  if (!cycle) return null

  const cycleKey = String(cycle.id ?? '')
  if (cycleKey && cache.has(cycleKey)) {
    return cache.get(cycleKey)
  }

  const durationDays = getCycleDurationDaysFromLengthValue(cycle.length_value)

  // Prefer length_value + anchor chain — API projected_study_* can be off-by-one vs duration.
  if (durationDays) {
    const occurGapDays = getCycleOccurGapDaysFromStartTime(cycle.start_time)
    const anchorId = getCycleAnchorId(cycle)
    const anchorOption = normalizeCycleAnchorOption(cycle.metadata?.anchor_option, anchorId)

    let projectedStart = occurGapDays + 1

    if (anchorOption === 'cycle' && anchorId) {
      const anchor = allCycles.find(c => String(c.id) === String(anchorId))
      if (!anchor) {
        if (cycleKey) cache.set(cycleKey, null)
        return null
      }
      const anchorProjected = resolveProjectedStudyDaysForCycle(anchor, allCycles, cache)
      if (!anchorProjected) {
        if (cycleKey) cache.set(cycleKey, null)
        return null
      }
      projectedStart = anchorProjected.projected_study_end_day + occurGapDays
    } else if (anchorOption === 'visit') {
      const visitDay = Number(cycle.metadata?.anchor_visit_study_day)
      if (Number.isFinite(visitDay)) {
        projectedStart = visitDay + occurGapDays
      }
    }

    const result = {
      projected_study_day: projectedStart,
      projected_study_end_day: projectedStart + durationDays,
    }
    if (cycleKey) cache.set(cycleKey, result)
    return result
  }

  const metaStart = cycle.metadata?.projected_study_day
  const metaEnd = cycle.metadata?.projected_study_end_day
  if (metaStart != null && metaStart !== '' && metaEnd != null && metaEnd !== '') {
    const fromMeta = {
      projected_study_day: Number(metaStart),
      projected_study_end_day: Number(metaEnd),
    }
    if (cycleKey) cache.set(cycleKey, fromMeta)
    return fromMeta
  }

  if (cycleKey) cache.set(cycleKey, null)
  return null
}

export const getCyclesWithResolvedProjectedDays = (cycles = []) => {
  const nonDummy = cycles.filter(cycle => !cycle?.is_dummy)
  return cycles.map(cycle => {
    if (cycle?.is_dummy) return cycle
    const resolved = resolveProjectedStudyDaysForCycle(cycle, nonDummy)
    if (!resolved) return cycle
    return {
      ...cycle,
      metadata: {
        ...cycle.metadata,
        projected_study_day: resolved.projected_study_day,
        projected_study_end_day: resolved.projected_study_end_day,
      },
    }
  })
}

export const getSelectedCycleDurationDays = ({ allCycles = [], selectedCycleId }) => {
  if (selectedCycleId == null || selectedCycleId === '') return null

  const selectedCycle = getCyclesWithResolvedProjectedDays(allCycles).find(
    cycle => String(cycle?.id) === String(selectedCycleId),
  )
  if (!selectedCycle) return null

  const durationFromLength = getCycleDurationDaysFromLengthValue(selectedCycle.length_value)
  if (durationFromLength != null) return durationFromLength

  const projectedStartDay = selectedCycle.metadata?.projected_study_day
  const projectedEndDay = selectedCycle.metadata?.projected_study_end_day
  if (
    projectedStartDay == null ||
    projectedStartDay === '' ||
    projectedEndDay == null ||
    projectedEndDay === ''
  ) {
    return null
  }

  const durationDays = Number(projectedEndDay) - Number(projectedStartDay)
  return Number.isFinite(durationDays) && durationDays > 0 ? durationDays : null
}

export const doesVisitDayExceedSelectedCycleDuration = ({ allCycles = [], selectedCycleId, visitDayValue }) => {
  const selectedCycleDurationDays = getSelectedCycleDurationDays({ allCycles, selectedCycleId })
  const visitDayNumber = Number(visitDayValue)

  return (
    typeof selectedCycleDurationDays === 'number' &&
    Number.isFinite(selectedCycleDurationDays) &&
    Number.isFinite(visitDayNumber) &&
    visitDayNumber >= selectedCycleDurationDays
  )
}

/**
 * Validate cycle modal values against study duration (single cycle duration + chained cycles).
 * @returns {string} Error message or empty string
 */
export const getCycleStudyDurationValidationError = ({
  studyDurationDays,
  durationDays,
  occurDays,
  anchorOption,
  anchorCycleId,
  anchorVisitStudyDay,
  existingCycles = [],
  lengthValueMinutes,
  cycleId,
  errorMessageTemplate,
}) => {
  const normalizedAnchor = normalizeCycleAnchorOption(anchorOption, anchorCycleId)

  if (typeof studyDurationDays !== 'number' || !Number.isFinite(studyDurationDays) || studyDurationDays < 1) {
    return ''
  }

  const message = errorMessageTemplate.replace('{value}', String(studyDurationDays))

  if (durationDays > studyDurationDays) {
    return message
  }

  // eslint-disable-next-line no-use-before-define
  const draftCycle = buildDraftVisitCycle({
    durationDays,
    occurDays,
    anchorOption: normalizedAnchor,
    anchorCycleId,
    anchorVisitStudyDay,
    existingCycles,
    lengthValueMinutes,
    cycleId,
  })

  // eslint-disable-next-line no-use-before-define
  if (wouldVisitCyclesExceedStudyDuration([...existingCycles, draftCycle], studyDurationDays)) {
    return message
  }

  return ''
}

/**
 * Estimate projected study days for a cycle being created/edited in the modal (before save).
 * @returns {{ projected_study_day: number, projected_study_end_day: number }}
 */
export const estimateDraftCycleProjectedDays = ({
  durationDays,
  occurDays,
  anchorOption,
  anchorCycleId,
  anchorVisitStudyDay,
  existingCycles = [],
  lengthValueMinutes,
}) => {
  const normalizedAnchor = normalizeCycleAnchorOption(anchorOption, anchorCycleId)
  const duration = Math.max(1, Math.floor(Number(durationDays) || 0))
  const occurGapDays = Math.max(0, Math.floor(Number(occurDays) || 0))
  const lengthMinutes =
    lengthValueMinutes != null
      ? Number(lengthValueMinutes)
      : convertMinToDays(duration, true)

  const draftCycle = {
    id: '__draft_cycle__',
    length_value: lengthMinutes,
    start_time: convertMinToDays(occurGapDays, true),
    anchor_id: normalizedAnchor === 'cycle' ? anchorCycleId : '',
    metadata: {
      anchor_option: normalizedAnchor,
      ...(normalizedAnchor === 'visit' && Number.isFinite(Number(anchorVisitStudyDay))
        ? { anchor_visit_study_day: Number(anchorVisitStudyDay) }
        : {}),
    },
  }

  const resolved = resolveProjectedStudyDaysForCycle(draftCycle, existingCycles)
  if (resolved) return resolved

  return {
    projected_study_day: occurGapDays + 1,
    projected_study_end_day: occurGapDays + 1 + duration,
  }
}

/**
 * Build a cycle-shaped object for timeline validation from modal form values.
 */
export function buildDraftVisitCycle({
  durationDays,
  occurDays,
  anchorOption,
  anchorCycleId,
  anchorVisitStudyDay,
  existingCycles = [],
  lengthValueMinutes,
  cycleId,
}) {
  const normalizedAnchor = normalizeCycleAnchorOption(anchorOption, anchorCycleId)
  const occurGapDays = Math.max(0, Math.floor(Number(occurDays) || 0))
  const lengthMinutes =
    lengthValueMinutes != null
      ? Number(lengthValueMinutes)
      : convertMinToDays(Math.max(1, Math.floor(Number(durationDays) || 0)), true)

  const draftCycle = {
    id: cycleId || '__draft_cycle__',
    is_dummy: false,
    length_value: lengthMinutes,
    start_time: convertMinToDays(occurGapDays, true),
    anchor_id: normalizedAnchor === 'cycle' ? anchorCycleId : '',
    metadata: {
      anchor_option: normalizedAnchor,
      ...(normalizedAnchor === 'visit' && Number.isFinite(Number(anchorVisitStudyDay))
        ? { anchor_visit_study_day: Number(anchorVisitStudyDay) }
        : {}),
    },
  }

  const resolved = resolveProjectedStudyDaysForCycle(draftCycle, existingCycles)
  return {
    ...draftCycle,
    metadata: {
      ...draftCycle.metadata,
      ...(resolved || estimateDraftCycleProjectedDays({
        durationDays,
        occurDays,
        anchorOption,
        anchorCycleId,
        anchorVisitStudyDay,
        existingCycles,
        lengthValueMinutes,
      })),
    },
  }
}

/**
 * Last occupied study day across cycles (from resolved projected_study_end_day, which is exclusive).
 * @param {Array} cycles
 * @returns {number}
 */
export const getMaxInclusiveStudyDayFromCycles = (cycles = []) => {
  const resolved = getCyclesWithResolvedProjectedDays(cycles).filter(cycle => !cycle?.is_dummy)
  if (!resolved.length) return 0
  return Math.max(
    ...resolved.map(cycle => {
      const exclusiveEnd = cycle.metadata?.projected_study_end_day
      if (exclusiveEnd == null || exclusiveEnd === '') return 0
      return Math.max(1, Number(exclusiveEnd) - 1)
    }),
  )
}

/**
 * True when the combined configured cycles (incl. draft) extend past study duration.
 * @param {Array} cycles
 * @param {number | undefined | null} studyDurationDays
 * @returns {boolean}
 */
export function wouldVisitCyclesExceedStudyDuration(cycles = [], studyDurationDays) {
  if (typeof studyDurationDays !== 'number' || !Number.isFinite(studyDurationDays) || studyDurationDays < 1) {
    return false
  }
  return getMaxInclusiveStudyDayFromCycles(cycles) > studyDurationDays
}

/**
 * Build inclusive study-day ranges for configured visit cycles (non-dummy), aligned with StudyTimeline.
 * @param {Array} cycles
 * @returns {Array<{ startDay: number, endDay: number }>}
 */
export const getVisitCycleStudyDayRanges = (cycles = []) => {
  const nonDummyCycles = getCyclesWithResolvedProjectedDays(cycles).filter(cycle => !cycle?.is_dummy)
  const rawRanges = []

  nonDummyCycles.forEach(cycle => {
    const projectedStartDay = cycle.metadata?.projected_study_day
    const projectedEndDay = cycle.metadata?.projected_study_end_day

    if (
      projectedStartDay !== undefined &&
      projectedStartDay !== null &&
      projectedEndDay !== undefined &&
      projectedEndDay !== null
    ) {
      const startDay = Math.max(1, projectedStartDay)
      // projected_study_end_day is exclusive (first day after cycle); last occupied day = end - 1
      const endDay = Math.max(startDay, Number(projectedEndDay) - 1)
      rawRanges.push({ startDay, endDay })
    }
  })

  rawRanges.sort((a, b) => a.startDay - b.startDay)
  const adjustedRanges = []
  for (let i = 0; i < rawRanges.length; i++) {
    const prev = i > 0 ? adjustedRanges[i - 1] : null
    let { startDay, endDay } = rawRanges[i]
    if (prev != null && startDay <= prev.endDay) {
      const delta = prev.endDay + 1 - startDay
      startDay = prev.endDay + 1
      endDay += delta
    }
    adjustedRanges.push({ startDay, endDay })
  }
  return adjustedRanges
}

/**
 * Visits eligible as cycle anchors: non-cycled (dummy-cycle) visits and visits that start a cycle.
 * @param {Array} phasesList
 * @returns {Array}
 */
export const getCycleAnchorEligibleVisits = (phasesList = []) => {
  const cycles = phasesList.reduce((acc, phase) => [...acc, ...(phase?.cycles || [])], [])
  const visitsById = new Map()

  cycles.forEach(cycle => {
    const cycleVisits = Array.isArray(cycle?.visits) ? cycle.visits : []
    cycleVisits.forEach(visit => {
      if (!visit?.id) return
      const deploymentOption = visit?.metadata?.deployment_option
      const isEligible = cycle?.is_dummy || deploymentOption === 'startsCycle'
      if (isEligible && !visitsById.has(visit.id)) {
        visitsById.set(visit.id, visit)
      }
    })
  })

  return Array.from(visitsById.values())
}

export const formatCycleAnchorVisitOptions = (visits = []) =>
  visits.map(visit => ({
    key: visit.id,
    text: visit.name,
  }))

/**
 * True when configured visit cycles collectively cover the full study duration.
 * @param {Array} cycles
 * @param {number | undefined | null} studyDurationDays
 * @returns {boolean}
 */
export const doVisitCyclesSpanFullStudyDuration = (cycles = [], studyDurationDays) => {
  if (typeof studyDurationDays !== 'number' || !Number.isFinite(studyDurationDays) || studyDurationDays < 1) {
    return false
  }
  return getMaxInclusiveStudyDayFromCycles(cycles) >= studyDurationDays
}

export default calculateVisitDay
